import { sendNotification, ViewCustomAction, ViewCustomActionArgs } from '@msdk/common'
import { DefaultActionType, EntityMetadata, readBaseMetadataByName, ServerOperationResult } from '@msdk/definitions'
import { ActionConfirmationModal, resolveConfig } from '@msdk/forms'
import React from 'react'

export class DeleteAction extends ViewCustomAction {
  private title: string
  private body: string
  private success: string
  private error: string
  private confirmationLabel: string

  constructor(args: ViewCustomActionArgs) {
    super(args)
    this.title = this.translate('VIEW_ACTION_TITLE_DELETE', 'Delete permanently')
    this.body = this.translate('VIEW_ACTION_DESCRIPTION_DELETE', 'Are you sure?{break}The action cannot be reversed.')
    this.success = this.translate('VIEW_ACTION_SUCCESS_DELETE', 'The items were deleted successfully')
    this.error = this.translate('VIEW_ACTION_ERROR_DELETE', 'The items delete failed')
    this.confirmationLabel = this.translate('VIEW_ACTION_DELETE_CONFIRMATION_LABEL', 'Delete')
  }

  public onInit(): Promise<void> {
    const config = resolveConfig(this.config)

    const titleIdml = config['TITLE_IDML']
    const titleDesc = config['TITLE_DESCRIPTION']
    if (titleIdml && titleDesc) this.title = this.translate(titleIdml, titleDesc)

    const bodyIdml = config['BODY_IDML']
    const bodyDesc = config['BODY_DESCRIPTION']
    if (bodyIdml && bodyDesc) this.body = this.translate(bodyIdml, bodyDesc)

    const successIdml = config['SUCCESS_IDML']
    const successDesc = config['SUCCESS_DESCRIPTION']
    if (successIdml && successDesc) this.success = this.translate(successIdml, successDesc)

    const errorIdml = config['ERROR_IDML']
    const errorDesc = config['ERROR_DESCRIPTION']
    if (errorIdml && errorDesc) this.error = this.translate(errorIdml, errorDesc)

    const confirmIdml = config['CONFIRM_IDML']
    const confirmDesc = config['CONFIRM_DESCRIPTION']
    if (confirmIdml && confirmDesc) this.confirmationLabel = this.translate(confirmIdml, confirmDesc)

    return Promise.resolve()
  }

  async onClick(): Promise<void> {
    await this.handleClick()
  }

  onConfirm = async () => {
    const promises: Promise<{
      message?: string | React.ReactNode
      status: ServerOperationResult
    }>[] = []
    this.selectedRows.forEach((row) => {
      const baseMetadata = readBaseMetadataByName(row.entityName) as EntityMetadata
      const promise = (async () => {
        try {
          await this.apiClient.entity.deleteEntity(baseMetadata.msProvider, row.entityName, row.id)
          return {
            status: ServerOperationResult.Success,
          }
        } catch (error) {
          if (error instanceof Error) {
            const regex = /\{[^]*\}/
            const match = error.message.match(regex)

            if (match) {
              const jsonString = match[0]
              const errorObject = JSON.parse(jsonString)
              if (errorObject.status == '409') {
                return {
                  message: errorObject.message,
                  status: ServerOperationResult.Failure,
                }
              }
            }
          }

          return {
            message: this.error,
            status: ServerOperationResult.Failure,
          }
        }
      })()
      promises.push(promise)
    })

    const res = await Promise.all(promises)
    const errors = res.filter((r) => r.status === ServerOperationResult.Failure).map((r) => r.message)

    if (errors.length) {
      for (const error of errors) {
        sendNotification({ message: error, status: 'error' })
      }
    } else {
      sendNotification({ message: this.success, status: 'success' })
    }
    this.reload()
  }

  handleClick = async () => {
    const modal = this.componentModal.createModal(
      <ActionConfirmationModal
        title={this.title}
        body={this.body}
        onConfirm={this.onConfirm}
        confirmationLabel={this.confirmationLabel}
        type={DefaultActionType.Delete}
      />,
      () => {
        //do nothing
      },
    )

    modal.open()
  }

  isDisabled(): boolean {
    return this.selectedRows.length === 0
  }

  isVisible(): boolean {
    return this.acl?.canDelete ?? false
  }
}
